import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('storage.service')

import { getConfig } from "@/config"

/**
 * Service for storing and retrieving data from localStorage
 */
export class StorageService {
  /**
   * The prefix for the storage keys
   */
  private prefix: string

  constructor() {
    this.prefix = `${getConfig().app.id}_`
  }

  /**
   * Set a value in localStorage
   * @param key - The key to set the value for
   * @param value - The value to set
   */
  async set(key: string, value: any): Promise<void> {
    try {
      // Special handling for auth_token to ensure it's properly stored
      if (key === 'auth_token') {
        fileLogger.log(`[StorageService] Storing auth_token (prefixed key: ${this.prefix + key})`)
        localStorage.setItem(this.prefix + key, JSON.stringify(value))
        // Verify token was properly stored
        const storedValue = localStorage.getItem(this.prefix + key)
        fileLogger.log(`[StorageService] Verified auth_token storage:`, 
                    storedValue ? `Success (type: ${typeof storedValue})` : 'Failed')
        return
      }
      
      const serializedValue = JSON.stringify(value)
      const storageKey = this.prefix + key
      localStorage.setItem(storageKey, serializedValue)
      
      // Verify the value was stored correctly (especially for bookmarks)
      if (key === 'bookmarks') {
        const storedValue = localStorage.getItem(storageKey)
        if (storedValue) {
          try {
            const parsed = JSON.parse(storedValue)
            fileLogger.log(`[StorageService] Verified bookmarks storage: ${Array.isArray(parsed) ? parsed.length : 'invalid'} items`)
          } catch (e) {
            fileLogger.error(`[StorageService] Failed to verify bookmarks storage:`, e)
          }
        } else {
          fileLogger.error(`[StorageService] Bookmarks storage verification failed: no value stored`)
        }
      }
    } catch (error) {
      fileLogger.error('Error saving to storage:', error)
      throw error // Re-throw to ensure callers know the save failed
    }
  }

  /**
   * Get a value from localStorage
   * @param key - The key to get the value for
   * @returns The value from localStorage or null if it doesn't exist
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const storageKey = this.prefix + key
      const value = localStorage.getItem(storageKey)
      
      // Special handling for auth_token with detailed logging
      if (key === 'auth_token') {
        if (value) {
          try {
            const parsed = JSON.parse(value)
            return parsed as T
          } catch (parseError) {
            fileLogger.error('Error parsing auth_token:', parseError)
            return null
          }
        }
        return null
      }
      
      return value ? JSON.parse(value) : null
    } catch (error) {
      fileLogger.error('Error reading from storage:', error)
      return null
    }
  }

  /**
   * Remove a value from localStorage
   * @param key - The key to remove the value for
   */
  async remove(key: string): Promise<void> {
    try {
      localStorage.removeItem(this.prefix + key)
    } catch (error) {
      fileLogger.error('Error removing from storage:', error)
    }
  }

  /**
   * Clear all values from localStorage
   */
  async clear(): Promise<void> {
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(this.prefix))
        .forEach(key => localStorage.removeItem(key))
    } catch (error) {
      fileLogger.error('Error clearing storage:', error)
    }
  }
} 